import { ChangeEvent, Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { ExpandMore } from "@mui/icons-material";
import { Chip, Grid2, IconButton, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DetailedServiceBookingData } from "../Domain/RemoteOrderRequests.types";
import useServiceTypeColor from "../hooks/useServiceTypeColor";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_Row,
  MRT_RowSelectionState,
  useMaterialReactTable
} from "material-react-table";
import { OrderRequestBookableEquipmentRow } from "../Domain/OrderRequestList.types";
import { renderCateringPricePerUnitCell } from "../../Catering/catering-list.partial";
import {
  renderServiceBookingLeadTime,
  renderServiceImage,
  renderServiceSetupTime
} from "../../../FacilityManager/Components/Toolbars/InfoSidebar/ZoneInfoSidebar/ServicePartial/ServiceCommonRender.partial";
import { commonLocalization } from "../../../../functions/tableLocalization";
import { handleInitialItemSelect } from "../functions/handleInitialItemSelect.function";
import { DetailDataChanges } from "./OrderRequestDetailDialog.partial";
import { BookingRequestsStatus } from "../../../BookingRequest/typings/booking-requests.types";
import {
  ServiceDeliveryStatus,
  ServiceType
} from "../../../../features/Booking-Form/typings/booking-service-administration.types";
import { statusEditSelectOpts } from "./OrderRequestCardItem.partial";
import { renderServiceLineStatusLabelCell } from "./OrderRequestDetailServiceLineStatusCell.partial";
import { EnlargedImageDialog } from "../../../../features/EnlargedImageCarousel/enlarged-image-dialog.component";
import { withSeeditMrtInitialState } from "../../../../themes/mrt-table-theme";
import { useSeeditMrtTableOptions } from "../../../../themes/use-seedit-mrt-table-options";

type Props = {
  listOpen: boolean;
  setListOpen: (value: SetStateAction<{ [key: number]: boolean }>) => void;
  detailData: DetailedServiceBookingData;
  editedData: DetailedServiceBookingData | null;
  handleDetailData: (changes: DetailDataChanges) => void;
  equipmentRowSelection: MRT_RowSelectionState;
  setEquipmentRowSelection: Dispatch<SetStateAction<MRT_RowSelectionState>>;
  isArchive: boolean;
  isBookingCancelled: boolean;
  isPostLoading: boolean;
  chipStyle: any;
  /** Service admin can edit when order progress is READY (same as catering available qty). */
  isServiceAdminBypass?: boolean;
  /** Standalone resource equipment orders hide setup time. */
  isResourceEquipmentOrder?: boolean;
};

export function OrderRequestDetailBookableEquipmentTable({
  listOpen,
  setListOpen,
  detailData,
  editedData,
  handleDetailData,
  equipmentRowSelection,
  setEquipmentRowSelection,
  isArchive,
  isBookingCancelled,
  isPostLoading,
  chipStyle,
  isServiceAdminBypass = false,
  isResourceEquipmentOrder = false
}: Readonly<Props>) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { equipmentColor } = useServiceTypeColor();

  const [equipmentData, setEquipmentData] = useState<OrderRequestBookableEquipmentRow[]>([]);

  const [showBiggerImage, setShowBiggerImage] = useState<string | null>(null);

  const handleChangeStatus = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    row: MRT_Row<OrderRequestBookableEquipmentRow>
  ) => {
    handleDetailData({
      type: ServiceType.Bookable_Equipment,
      id: row.original.id,
      originalQty: row.original.quantity,
      newQty: row.original.quantity,
      status: event.target.value as BookingRequestsStatus
    });
  };

  useEffect(() => {
    if (!editedData) return;
    setEquipmentData(editedData.equipments);
    handleInitialItemSelect(
      isArchive,
      isBookingCancelled,
      editedData.equipments,
      setEquipmentRowSelection
    );
  }, [isArchive, isBookingCancelled, editedData?.equipments]);

  const column = useMemo<MRT_ColumnDef<OrderRequestBookableEquipmentRow>[]>(
    () => [
      {
        header: t("Name"),
        accessorKey: "name",
        enableEditing: false,
        size: 200
      },
      {
        accessorKey: "status",
        header: t("Status"),
        Cell: ({ cell }) => renderServiceLineStatusLabelCell(cell.row.original.status, t),
        size: 100,
        editVariant: "select",
        muiEditTextFieldProps: ({ row }) => {
          return {
            onChange: event => handleChangeStatus(event, row)
          };
        },
        editSelectOptions: () => statusEditSelectOpts(),
        enableEditing: row => row.original.status !== BookingRequestsStatus.CANCELLED
      },
      {
        header: t("Description"),
        accessorKey: "description",
        enableEditing: false,
        size: 250
      },
      {
        header: t("Cost (h)"),
        accessorKey: "costPerHour",
        Cell: ({ row: { original } }) => renderCateringPricePerUnitCell(original.costPerHour ?? 0),
        enableEditing: false,
        size: 50
      },
      {
        header: t("Booking Lead Time"),
        accessorKey: "bookingLeadTime",
        Cell: ({ row: { original } }) => <Typography>{original.bookingLeadTime ?? 0}</Typography>,
        enableEditing: false,
        size: 50,
        Header: renderServiceBookingLeadTime()
      },
      ...(isResourceEquipmentOrder
        ? []
        : [
            {
              header: t("Setup Time"),
              accessorKey: "setupTime",
              enableEditing: false,
              size: 50,
              Header: renderServiceSetupTime()
            } satisfies MRT_ColumnDef<OrderRequestBookableEquipmentRow>
          ]),
      {
        accessorKey: "image",
        header: t("Image"),
        enableEditing: false,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => renderServiceImage(100, cell.row.original.image, setShowBiggerImage),
        size: 100
      }
    ],
    [equipmentData, isResourceEquipmentOrder, t]
  );

  const deliveryLockedForEditing =
    editedData?.deliveryStatus === ServiceDeliveryStatus.READY && !isServiceAdminBypass;
  const seeditMrtOptions = useSeeditMrtTableOptions<OrderRequestBookableEquipmentRow>();

  const table = useMaterialReactTable({
    ...seeditMrtOptions,
    columns: column,
    data: editedData?.equipments ?? [],
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: true,
    autoResetPageIndex: false,
    enableRowSelection: false,
    editDisplayMode: "table",
    enableEditing: !isArchive && !isBookingCancelled && !deliveryLockedForEditing,
    getRowId: row => row.id?.toString(),
    onRowSelectionChange: setEquipmentRowSelection,
    state: {
      rowSelection: equipmentRowSelection,
      isSaving: isPostLoading
    },
    initialState: withSeeditMrtInitialState({
      showColumnFilters: true,
      showGlobalFilter: true,
      pagination: { pageSize: 10, pageIndex: 0 }
    }),
    positionToolbarAlertBanner: "none",
    muiPaginationProps: {
      rowsPerPageOptions: [5, 10, 15, 20, 30],
      variant: "outlined"
    },
    paginationDisplayMode: "pages",
    localization: {
      ...commonLocalization(t),
      noRecordsToDisplay: t("There are no equipments")
    }
  });

  return (
    <>
      <Grid2 container sx={{ width: "100%", flexDirection: "column" }}>
        <Grid2>
          {/* header of bookable equipments table */}
          <IconButton
            data-testid={"expandButton"}
            sx={[
              {
                transition: theme.transitions.create("transform", {
                  duration: theme.transitions.duration.shortest
                })
              },
              listOpen ? { transform: "rotate(0deg)" } : { transform: "rotate(-90deg)" }
            ]}
            onClick={() => setListOpen(prev => ({ ...prev, [1]: !prev[1] }))}
          >
            <ExpandMore />
          </IconButton>

          <Chip
            label={t("OrderRequest_Equipment", {
              number:
                detailData.numberOfBookableEquipments > 0
                  ? detailData.numberOfBookableEquipments
                  : detailData.equipments?.length ?? 1
            })}
            sx={{ ...chipStyle, backgroundColor: equipmentColor }}
          />
        </Grid2>

        {/* bookable equipments table */}
        <Grid2 sx={{ pl: 5 }}>
          <MaterialReactTable table={table} />
        </Grid2>
      </Grid2>
      <EnlargedImageDialog
        open={!!showBiggerImage}
        picturePath={showBiggerImage}
        handleClose={() => setShowBiggerImage(null)}
      />
    </>
  );
}
